/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  QUERYWMS_GETCAPABILITIES,
  QUERYWMS_LAYER,
  QUERYWMS_LAYERS,
  QUERYWMS_LAYERSTREE,
  QUERYWMS_SERVICEINFO,
  QUERYWMS_OGCTYPE,
  queryWMSKeys,
} from './queryConstants';
import type {
  OGCWMSWMTSGetCapabilities,
  LayerProps,
  LayerTree,
  WMSServiceInfo,
  WMTSGetCapabilities,
  WMSGetCapabilities,
} from '../components/types';
import { OGCTYPE } from '../components/types';
import WMXMLParser from '../components/WMXMLParser';
import {
  handleWMTSGetCapabilities,
  makeQueryKeyFroMServiceUrl,
  privateGetWMSServiceInfo,
  privateGetWMTSServiceInfo,
  privateWmsFlattenLayerTree,
  privateWmsGetCapabilities,
  privateWmsGetLayerTree,
} from '../utils/getCapabilities/utils';
import { wmsQueryClient } from '../utils/getCapabilities/wmsQueryClient';

/**
 * Invalidates the tanstack store for this getcapabilities service. Used to force refetch.
 *
 * @async
 * @param {string} serviceUrl
 * @returns {Promise<void>}
 */
export const invalidateWMSGetCapabilities = async (
  serviceUrl: string,
): Promise<void> => {
  await Promise.all(
    queryWMSKeys.map((queryWMSKey) => {
      return wmsQueryClient.invalidateQueries({
        queryKey: [makeQueryKeyFroMServiceUrl(serviceUrl), queryWMSKey],
      });
    }),
  );
};

// For WMTS, we can use the same invalidate function (same keys/urls)
export const invalidateWMTSGetCapabilities = invalidateWMSGetCapabilities;

/**
 * Determines whether the service is WMS or WMTS
 *
 * @async
 * @param {string} serviceUrl
 * @returns {Promise<string>}
 */
export const queryOGCTypeWMSorWMTS = async (
  serviceUrl: string,
): Promise<OGCTYPE> => {
  const data = await queryWMSGetCapabilities(serviceUrl);
  return wmsQueryClient.fetchQuery({
    queryKey: [makeQueryKeyFroMServiceUrl(serviceUrl), QUERYWMS_OGCTYPE],
    queryFn: () => {
      const WMTS =
        (data as WMTSGetCapabilities).Capabilities?.ServiceIdentification
          ?.ServiceType?.value === 'OGC WMTS';
      return WMTS ? OGCTYPE.WMTS : OGCTYPE.WMS;
    },
  });
};

/**
 * Fetches the WMS GetCapabilities document and converts it to a json object. It is cached by TanStack.
 *
 * @async
 * @param {string} serviceUrl
 * @returns {Promise<OGCWMSWMTSGetCapabilities>}
 */
export const queryWMSGetCapabilities = async (
  serviceUrl: string,
): Promise<OGCWMSWMTSGetCapabilities> => {
  return wmsQueryClient.fetchQuery({
    queryKey: [
      makeQueryKeyFroMServiceUrl(serviceUrl),
      QUERYWMS_GETCAPABILITIES,
    ],
    queryFn: () => privateWmsGetCapabilities(serviceUrl),
  });
};

/**
 * Gets the layer tree in hierarchical order for a WMS service. Cached by TanStack. Uses the same store as in queryWMSGetCapabilities.
 *
 * @async
 * @param {string} serviceUrl
 * @returns {Promise<LayerTree>}
 */
export const queryWMSLayersTree = async (
  serviceUrl: string,
): Promise<LayerTree> => {
  const data = await queryWMSGetCapabilities(serviceUrl);
  return wmsQueryClient.fetchQuery({
    queryKey: [makeQueryKeyFroMServiceUrl(serviceUrl), QUERYWMS_LAYERSTREE],
    queryFn: () => privateWmsGetLayerTree(data),
  });
};

/**
 * Returns details about the WMS service
 *
 * @async
 * @param {string} serviceUrl
 * @returns {Promise<WMSServiceInfo>}
 */
export const queryWMSServiceInfo = async (
  serviceUrl: string,
): Promise<WMSServiceInfo> => {
  const ogcType = await queryOGCTypeWMSorWMTS(serviceUrl);
  const data =
    ogcType === OGCTYPE.WMS
      ? await queryWMSGetCapabilities(serviceUrl)
      : await queryWMTSGetCapabilities(serviceUrl);

  return wmsQueryClient.fetchQuery({
    queryKey: [makeQueryKeyFroMServiceUrl(serviceUrl), QUERYWMS_SERVICEINFO],
    queryFn: () => {
      return ogcType === OGCTYPE.WMS
        ? privateGetWMSServiceInfo(data as WMSGetCapabilities, serviceUrl)
        : privateGetWMTSServiceInfo(data as WMTSGetCapabilities, serviceUrl);
    },
  });
};

/**
 * Gets the flat layer list (without hierarchy) for a WMS service. Cached by TanStack. Uses the same store as in queryWMSGetCapabilities.
 *
 * @async
 * @param {string} serviceUrl
 * @param {boolean} [forceReload=false]
 * @returns {Promise<LayerProps[]>}
 */
export const queryWMSLayers = async (
  serviceUrl: string,
  forceReload = false,
): Promise<LayerProps[]> => {
  if (forceReload) {
    await invalidateWMSGetCapabilities(serviceUrl);
  }
  const ogcType = await queryOGCTypeWMSorWMTS(serviceUrl);
  const data =
    ogcType === OGCTYPE.WMS
      ? await queryWMSLayersTree(serviceUrl)
      : await queryWMTSGetCapabilities(serviceUrl);
  return wmsQueryClient.fetchQuery({
    queryKey: [makeQueryKeyFroMServiceUrl(serviceUrl), QUERYWMS_LAYERS],
    queryFn: () =>
      ogcType === OGCTYPE.WMS
        ? privateWmsFlattenLayerTree(data as LayerTree)
        : handleWMTSGetCapabilities(data as WMTSGetCapabilities),
  });
};

/**
 * Returns specific layer from the WMS service
 *
 * @async
 * @param {string} serviceUrl
 * @param {string} name
 * @returns {Promise<LayerProps>}
 */
export const queryWMSLayer = async (
  serviceUrl: string,
  name: string,
  forceReload = false,
): Promise<LayerProps> => {
  const queryWMSKey = [
    makeQueryKeyFroMServiceUrl(serviceUrl),
    QUERYWMS_LAYER,
    name,
  ];
  if (forceReload) {
    await wmsQueryClient.invalidateQueries({
      queryKey: queryWMSKey,
    });
  }
  const data = await queryWMSLayers(serviceUrl);
  return wmsQueryClient.fetchQuery({
    queryKey: queryWMSKey,
    queryFn: () => {
      return data.find((layer) => layer.name === name) || ({} as LayerProps);
    },
  });
};

/**
 * Fetches the WMTS GetCapabilities document and converts it to a json object. It is cached by TanStack.
 *
 * @async
 * @param {string} serviceUrl
 * @returns {Promise<WMTSGetCapabilities>}
 */
export const queryWMTSGetCapabilities = async (
  serviceUrl: string,
): Promise<WMTSGetCapabilities> => {
  return wmsQueryClient.fetchQuery({
    queryKey: [
      makeQueryKeyFroMServiceUrl(serviceUrl),
      QUERYWMS_GETCAPABILITIES,
    ],
    queryFn: () => {
      return WMXMLParser(serviceUrl, []);
    },
  });
};
